import { useMemo, useState } from 'react'
import { formatMoney } from '../../lib/format.js'
import BreakdownShell, { SortHeader, useSortedUnits } from './BreakdownShell.jsx'

// LaborBreakdownTable — by-unit labor cost today + labor %.
// The footer labor % is the WEIGHTED group figure (Σ labor / Σ sales),
// matching the headline KPI — not an average of the per-unit ratios.

function laborTone(pct) {
  if (pct == null) return 'text-black/40'
  if (pct < 30) return 'text-emerald-700'
  if (pct <= 35) return 'text-amber-700'
  return 'text-red-700'
}

export default function LaborBreakdownTable({ perUnit, onBack }) {
  // --- All hooks BEFORE any conditional return (React #310). ---
  const [sortKey, setSortKey] = useState('labor_cost_today')
  const [sortDir, setSortDir] = useState('desc')
  const rows = useSortedUnits(perUnit, sortKey, sortDir)

  const { totalLabor, totalSales } = useMemo(() => {
    let labor = 0, sales = 0
    for (const r of rows) {
      labor += Number(r.labor_cost_today) || 0
      sales += Number(r.sales_today) || 0
    }
    return { totalLabor: labor, totalSales: sales }
  }, [rows])
  const weightedPct = totalSales > 0 ? Math.round((totalLabor / totalSales) * 1000) / 10 : null

  function toggle(key) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(key); setSortDir('desc') }
  }

  return (
    <BreakdownShell title="Labor by location" onBack={onBack}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-black/10 text-left text-[11px] uppercase tracking-wide text-black/55">
            <SortHeader label="Location" k="name" sortKey={sortKey} sortDir={sortDir} onClick={toggle} />
            <SortHeader label="Labor today" k="labor_cost_today" sortKey={sortKey} sortDir={sortDir} onClick={toggle} align="right" />
            <SortHeader label="Labor %" k="labor_pct" sortKey={sortKey} sortDir={sortDir} onClick={toggle} align="right" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-black/5 last:border-b-0">
              <td className="py-2.5 pr-3 font-medium text-black/85">{r.name}</td>
              <td className="py-2.5 pl-3 text-right tabular-nums text-black/85">{formatMoney(r.labor_cost_today)}</td>
              <td className={`py-2.5 pl-3 text-right tabular-nums ${laborTone(r.labor_pct)}`}>
                {r.labor_pct == null ? '—' : `${r.labor_pct}%`}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-black/15 font-semibold">
            <td className="py-2.5 pr-3 text-black/85">All units</td>
            <td className="py-2.5 pl-3 text-right tabular-nums text-black/85">{formatMoney(totalLabor)}</td>
            <td className={`py-2.5 pl-3 text-right tabular-nums ${laborTone(weightedPct)}`}>
              {weightedPct == null ? '—' : `${weightedPct}%`}
            </td>
          </tr>
        </tfoot>
      </table>
    </BreakdownShell>
  )
}
